import React, { useRef } from 'react';

import { BRIDGE_KEYS, LayerDataBridge } from '../../../../shared/utils/layer-data-bridge';
import { useLayerManager } from '../../../../shared/hooks/use-layer-manager';
import { ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';

export type EditLayerBridgeData = {
  title: React.ReactNode;
  noTranslateTerms: string[];
  content: React.ReactNode;
  isDeactivating: boolean;
  onCloseAttempt: (() => void) | undefined;
  onGoBack: (() => void) | undefined;
};

type EditLayerRenderFn = (
  state: { dataBridge: LayerDataBridge<EditLayerBridgeData> },
  close: any,
) => JSX.Element;

type EditLayerInputData = {
  title: React.ReactNode;
  noTranslateTerms: string[];
  content: React.ReactNode;
  isDeactivating: boolean;
  changes: boolean;
  onGoBack: (() => void) | undefined;
};

export const useAccountSettingsEditLayer = (
  active: boolean,
  onClose: () => void,
  onDiscardClose: (closeFn: () => void) => void,
  renderFn: EditLayerRenderFn,
  data: EditLayerInputData,
): void => {
  const layerManager = useLayerManager();
  const hasOpenedRef = useRef(false);
  const layerHandleRef = useRef<{ close: () => void } | null>(null);
  const changesRef = useRef(false);
  const bridge = LayerDataBridge.getInstance<EditLayerBridgeData>(BRIDGE_KEYS.EDIT);

  changesRef.current = data.changes;

  const getBridgeData = (): EditLayerBridgeData => ({
    title: data.title,
    noTranslateTerms: data.noTranslateTerms,
    content: data.content,
    isDeactivating: data.isDeactivating,
    onGoBack: data.onGoBack
      ? () => {
          if (changesRef.current) onDiscardClose(() => data.onGoBack?.());
          else data.onGoBack?.();
        }
      : undefined,
    onCloseAttempt: data.isDeactivating
      ? undefined
      : () => {
          if (changesRef.current) onDiscardClose(() => layerHandleRef.current?.close());
          else layerHandleRef.current?.close();
        },
  });

  React.useEffect(() => {
    if (!active) {
      bridge.setData(null);
      return;
    }
    bridge.setData(getBridgeData());
  }, [active, data.title, data.content, data.isDeactivating, data.onGoBack]);

  React.useEffect(() => {
    if (active && !hasOpenedRef.current && layerManager) {
      hasOpenedRef.current = true;
      bridge.setData(getBridgeData());
      layerHandleRef.current = layerManager.openInteractionLayer(
        renderFn,
        { dataBridge: bridge },
        {
          size: 'E' as any,
          shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
          onClose: (_state: any, _response: any) => {
            hasOpenedRef.current = false;
            layerHandleRef.current = null;
            bridge.setData(null);
            onClose();
          },
        },
      );
    }
    if (!active) {
      hasOpenedRef.current = false;
      layerHandleRef.current?.close();
    }
  }, [active]);
};
